import AxiosInstance from '@/api/axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export interface AtlasSummary {
  /** Atlas-attributed leads whose sender received any agent message. */
  reached: number;
  /** ...of those, the ones who replied. */
  replied: number;
  /** PT consultations booked and still live (cancellations excluded). */
  ptBookings: number;
  /** ...and how many were cancelled, shown small beside the headline. */
  ptCancelled: number;
}

/** The three cards, and the three record lists behind them. */
export type AtlasMetric = 'reached' | 'replied' | 'pt';

export interface AtlasMemberRow {
  /** Needed by the delete action, which reuses DELETE /leads/:leadId. */
  _id: string;
  sender: string;
  name: string;
  phone: string;
  email: string;
  branchName: string;
  /** atlas_agent.date for members, created_date for PT — per the card. */
  date: string;
  /** PT only: the booked slot. Undefined for the member metrics. */
  appointment?: string;
  classType?: string;
  /** PT only: the trainer DataTrack booked (agentapis); null when unknown. */
  trainer?: string | null;
}

export interface AtlasPagination {
  currentPage: number;
  perpage: number;
  total: number;
  totalPages: number;
}

interface FetchArgs {
  organization_id?: string;
  branch_id?: string | null;
  region_id?: string | null;
  /** The canonical "YYYY-MM-DD - YYYY-MM-DD" string from generalFilterSlice. */
  date_range?: string;
}

const hasValue = (val?: string | null) =>
  Boolean(val && val.trim() !== '' && val !== 'all');

export const fetchAtlasSummary = createAsyncThunk(
  'atlasSummary/fetch',
  async (
    { organization_id, branch_id, region_id, date_range }: FetchArgs,
    thunkAPI
  ) => {
    // Without a branch or region the backend's access middleware refuses the
    // request for scoped roles, so asking would only produce a 403.
    if (!hasValue(branch_id) && !hasValue(region_id)) return null;

    try {
      const response = await AxiosInstance.get('/agents/atlas-summary', {
        params: { organization_id, branch_id, region_id, date_range },
        signal: thunkAPI.signal,
      });
      if (response.data.success) return response.data.data as AtlasSummary;
      return null;
    } catch (error: any) {
      if (thunkAPI.signal.aborted || error.code === 'ERR_CANCELED') {
        return thunkAPI.rejectWithValue('ABORTED');
      }
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error?.message || 'Request failed'
      );
    }
  }
);

/** The two CSV exports on the Atlas Members page (AN-860). */
export type AtlasExportType = 'members' | 'conversations' | 'pt';

/** One member: everything since their Atlas pickup. Dates are ISO strings. */
export interface AtlasExportMemberRow {
  name: string;
  phone: string;
  email: string;
  club: string;
  memberId: string;
  membershipType: string | null;
  isNonMember: boolean | null;
  pickedUp: string | null;
  firstMessageSent: string | null;
  replied: boolean;
  firstReplyAt: string | null;
  ptBooked: boolean;
  ptBookingStatus: 'Booked' | 'Cancelled' | null;
  ptBookingAt: string | null;
  trainer: string | null;
  smsUnsubscribed: boolean | null;
  emailUnsubscribed: boolean | null;
  marketingEmailOptIn: boolean | null;
  marketingSmsOptIn: boolean | null;
  privacyOptOut: boolean | null;
}

/** One message since pickup. Members sharing a contact are joined with " / ". */
export interface AtlasExportMessageRow {
  name: string;
  phone: string;
  email: string;
  club: string;
  memberId: string;
  replied: boolean;
  date: string;
  channel: string;
  from: 'Atlas' | 'Member' | 'Staff';
  message: string;
}

export type AtlasExportResult =
  | { type: 'members'; timezone: string | null; rows: AtlasExportMemberRow[] }
  | {
      type: 'conversations';
      timezone: string | null;
      rows: AtlasExportMessageRow[];
    }
  /** The PT Booked table's rows, every page. */
  | { type: 'pt'; timezone: string | null; rows: AtlasMemberRow[] };

/**
 * Rows for an Atlas CSV export. A plain request rather than a thunk: the rows
 * go straight into a file and never into the store. Null when there is no
 * branch or region selected, the same scope guard as the list.
 */
export async function requestAtlasExport(
  type: AtlasExportType,
  { organization_id, branch_id, region_id, date_range }: FetchArgs
): Promise<AtlasExportResult | null> {
  if (!hasValue(branch_id) && !hasValue(region_id)) return null;
  const response = await AxiosInstance.get('/agents/atlas-export', {
    params: { organization_id, branch_id, region_id, date_range, type },
  });
  return response.data.success
    ? (response.data.data as AtlasExportResult)
    : null;
}

interface FetchListArgs extends FetchArgs {
  metric?: AtlasMetric;
  page?: number;
  perpage?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const fetchAtlasList = createAsyncThunk(
  'atlasSummary/fetchList',
  async (args: FetchListArgs, thunkAPI) => {
    const { organization_id, branch_id, region_id, date_range } = args;

    // Same scope guard as the summary: without a branch or region the backend's
    // access middleware refuses the request for scoped roles.
    if (!hasValue(branch_id) && !hasValue(region_id)) return null;

    try {
      const response = await AxiosInstance.get('/agents/atlas-list', {
        params: {
          organization_id,
          branch_id,
          region_id,
          date_range,
          metric: args.metric || 'reached',
          page: args.page ?? 1,
          perpage: args.perpage ?? 10,
          search: args.search || undefined,
          sort: args.sort || undefined,
          order: args.order || undefined,
        },
        signal: thunkAPI.signal,
      });
      if (response.data.success) return response.data.data;
      return null;
    } catch (error: any) {
      if (thunkAPI.signal.aborted || error.code === 'ERR_CANCELED') {
        return thunkAPI.rejectWithValue('ABORTED');
      }
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error?.message || 'Request failed'
      );
    }
  }
);

interface AtlasSummaryState {
  data: AtlasSummary | null;
  loading: boolean;
  error: string | null;
  /** requestId of the in-flight fetch; older ones are ignored. */
  currentRequestId: string | null;
  rows: AtlasMemberRow[];
  pagination: AtlasPagination;
  timezone: string | null;
  listLoading: boolean;
  /** requestId of the in-flight list fetch; older ones are ignored. */
  listRequestId: string | null;
}

const emptyPagination: AtlasPagination = {
  currentPage: 1,
  perpage: 10,
  total: 0,
  totalPages: 1,
};

const initialState: AtlasSummaryState = {
  data: null,
  loading: false,
  error: null,
  currentRequestId: null,
  rows: [],
  pagination: emptyPagination,
  timezone: null,
  listLoading: false,
  listRequestId: null,
};

const atlasSummarySlice = createSlice({
  name: 'atlasSummary',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAtlasSummary.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAtlasSummary.fulfilled, (state, action) => {
        // A superseded response must not overwrite newer data — changing the
        // date range twice quickly would otherwise land the older numbers last.
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchAtlasList.pending, (state, action) => {
        state.listLoading = true;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchAtlasList.fulfilled, (state, action) => {
        // Superseded responses must not overwrite newer rows — switching metric
        // twice quickly would otherwise land the older page last.
        if (state.listRequestId !== action.meta.requestId) return;
        state.listRequestId = null;
        state.listLoading = false;
        state.rows = action.payload?.data ?? [];
        state.pagination = action.payload?.pagination ?? emptyPagination;
        state.timezone = action.payload?.timezone ?? null;
      })
      .addCase(fetchAtlasList.rejected, (state, action) => {
        if (action.payload === 'ABORTED') return;
        if (state.listRequestId !== action.meta.requestId) return;
        state.listRequestId = null;
        state.listLoading = false;
        state.rows = [];
        state.pagination = emptyPagination;
      })
      .addCase(fetchAtlasSummary.rejected, (state, action) => {
        if (action.payload === 'ABORTED') return;
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.loading = false;
        state.data = null;
        state.error =
          (action.payload as string) || 'Failed to load member support summary';
      });
  },
});

export default atlasSummarySlice.reducer;
